// ─────────────────────────────────────────────────────────────────────────────
// Graph ⇄ step-list conversion for the Workflow Engine builder (ADR-0011).
//
// WHY THIS FILE EXISTS
// The workflow is STORED as a directed graph (dbo.AI_WorkflowNode /
// AI_WorkflowEdge) because that is what the engine walks. But a lab describes a
// method as an ordered SEQUENCE of steps, and 15 of the 24 seeded workflows are
// purely linear — so a canvas makes the common case harder than it needs to be.
//
// The builder therefore edits an ordered step list, and this file is the
// translation both ways. The graph stays the single source of truth; the step
// list is a VIEW of it. Nothing about the storage model changes.
//
//   linearize()   graph  → steps   (open a workflow)
//   materialize() steps  → graph   (save it), with auto-layout positions
//
// Round-tripping is lossless for the shapes the builder can express (linear
// chains, decisions with two branches that rejoin, splits, attached QC). A
// graph drawn with something more exotic still opens — anything not reachable
// as a step lands in `orphans` so it is visible rather than silently dropped.
// ─────────────────────────────────────────────────────────────────────────────
import { CAT_BY_TYPE, isPrepFamily } from './catalog';
import type { WorkflowEdge, WorkflowNode } from './types';

/** One row in the builder. `qc` children hang off the step; `branch` nests. */
export interface Step {
  key: string;
  type: string;
  name: string;
  props: Record<string, unknown>;
  /** Terminal QC components attached to this step's batch. */
  qc: Step[];
  /** Present only on a branching component (decision / split). */
  branch?: { port: string; label: string; steps: Step[] }[];
  /** Canvas position, when the user has dragged this node. Absent = let the
   *  auto-layout decide. Purely cosmetic — it never affects the engine. */
  x?: number;
  y?: number;
}

export interface LinearizeResult {
  steps: Step[];
  /** Nodes not reachable as part of the sequence — surfaced, never dropped. */
  orphans: WorkflowNode[];
}

const parseProps = (json: string | null): Record<string, unknown> => {
  if (!json) return {};
  try { return JSON.parse(json) as Record<string, unknown>; }
  catch { return {}; }          // malformed JSON must not blank the whole step
};

const isTerminal = (type: string) => (CAT_BY_TYPE[type]?.outs.length ?? 1) === 0;
const isQc = (type: string) => type === 'qc';

/** graph → ordered steps. */
export function linearize(nodes: WorkflowNode[], edges: WorkflowEdge[]): LinearizeResult {
  const byKey = new Map(nodes.map(n => [n.nodeKey, n]));
  const out = new Map<string, WorkflowEdge[]>();
  for (const e of edges) {
    const list = out.get(e.fromNodeKey) ?? [];
    list.push(e);
    out.set(e.fromNodeKey, list);
  }

  const used = new Set<string>();

  const toStep = (n: WorkflowNode): Step => ({
    key: n.nodeKey,
    type: n.nodeType,
    name: n.nodeName ?? CAT_BY_TYPE[n.nodeType]?.label ?? n.nodeType,
    props: parseProps(n.propsJson),
    qc: [],
    // Stored coordinates are deliberately NOT read back. A workflow always
    // opens on a freshly computed layout, so it can never render with stale or
    // degenerate coordinates (an older save, or a row of nodes all written at
    // 0,0, would otherwise stack the whole graph on one spot — caught by the
    // "never stacks two nodes" test). Dragging sets x/y for the session and
    // those positions are persisted; re-opening re-derives a clean layout.
  });

  /** Every node reachable forward from `from`, used to find where two branches rejoin. */
  const reach = (from: string): Set<string> => {
    const seen = new Set<string>();
    const stack = [from];
    while (stack.length) {
      const c = stack.pop()!;
      if (!c || seen.has(c)) continue;
      seen.add(c);
      for (const e of out.get(c) ?? []) stack.push(e.toNodeKey);
    }
    return seen;
  };

  /** The first node both branches lead to — where the branch closes. */
  const joinOf = (starts: string[]): string | null => {
    const first = starts[0];
    if (starts.length < 2 || !first) return null;
    const sets = starts.map(reach);
    const firstSet = sets[0];
    if (!firstSet) return null;
    const common = [...firstSet].filter(k => sets.every(s => s.has(k)));
    if (!common.length) return null;
    // Prefer an explicit Merge; otherwise the one closest to the branch.
    const merge = common.find(k => byKey.get(k)?.nodeType === 'merge');
    if (merge) return merge;
    const order = [...reach(first)];
    return common.sort((a, b) => order.indexOf(a) - order.indexOf(b))[0] ?? null;
  };

  const walk = (from: string | null, stopAt: Set<string>): Step[] => {
    const steps: Step[] = [];
    let cur = from;
    let guard = 0;

    while (cur && !stopAt.has(cur) && guard++ < 500) {
      const node = byKey.get(cur);
      if (!node) break;
      if (used.has(cur)) break;            // a cycle should be impossible, but never hang
      used.add(cur);

      const step = toStep(node);
      const outs = out.get(cur) ?? [];

      // QC hangs off this step rather than being a stage in the sequence.
      for (const e of outs) {
        const child = byKey.get(e.toNodeKey);
        if (child && isQc(child.nodeType) && isTerminal(child.nodeType)) {
          used.add(child.nodeKey);
          step.qc.push(toStep(child));
        }
      }

      const flow = outs.filter(e => {
        const child = byKey.get(e.toNodeKey);
        return child && !(isQc(child.nodeType) && isTerminal(child.nodeType));
      });

      const cat = CAT_BY_TYPE[node.nodeType];
      const branching = (cat?.outs.length ?? 1) > 1;

      if (branching && flow.length > 1) {
        const join = joinOf(flow.map(e => e.toNodeKey));
        const inner = new Set(stopAt);
        if (join) inner.add(join);
        step.branch = flow.map(e => ({
          port: e.portKey,
          label: e.portKey.toUpperCase(),
          steps: walk(e.toNodeKey, inner),
        }));
        steps.push(step);
        cur = join;
        continue;
      }

      steps.push(step);
      cur = flow[0]?.toNodeKey ?? null;
    }
    return steps;
  };

  const start = nodes.find(n => n.nodeType === 'start') ?? nodes[0] ?? null;
  const steps = start ? walk(start.nodeKey, new Set()) : [];
  const orphans = nodes.filter(n => !used.has(n.nodeKey));
  return { steps, orphans };
}

/** steps → graph, with auto-layout. Node keys are preserved where they exist. */
export function materialize(steps: Step[]): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
  const nodes: WorkflowNode[] = [];
  const edges: WorkflowEdge[] = [];
  let seq = 0;
  const keyFor = (s: Step) => s.key || `n${++seq}`;

  const COL = 210, ROW = 84;

  /**
   * Occupancy map, keyed `col,row`.
   *
   * The previous layout put a branch's first lane at `row - 1`, which produced
   * NEGATIVE rows for a branch on the top row — the node was drawn above the
   * canvas origin and vanished off the top edge, taking its edges with it.
   * Placement now claims the requested cell and slides DOWN until it finds a
   * free one, so nothing overlaps and nothing is ever placed off-canvas.
   */
  const used = new Set<string>();
  const claim = (col: number, row: number): number => {
    let r = Math.max(0, row);
    while (used.has(`${col},${r}`)) r += 1;
    used.add(`${col},${r}`);
    return r;
  };

  const place = (s: Step, col: number, row: number) => {
    const r = claim(col, row);
    // A dragged node keeps its own position; everything else is auto-laid-out.
    return {
      x: s.x != null && s.y != null ? s.x : 40 + col * COL,
      y: s.x != null && s.y != null ? s.y : 30 + r * ROW,
      row: r,
    };
  };

  const emit = (s: Step, col: number, row: number): { key: string; row: number } => {
    const key = keyFor(s);
    // The whole preparation FAMILY carries a tier (Sample Prep · Extraction ·
    // Dilution), not just the legacy `prep` type — the Prep Methods view and
    // prep routing both read this column.
    const tier = isPrepFamily(s.type) ? Number(s.props.tier) || 1 : null;
    const pos = place(s, col, row);
    nodes.push({
      nodeKey: key,
      nodeType: s.type,
      nodeName: s.name,
      posX: pos.x,
      posY: pos.y,
      propsJson: JSON.stringify(s.props),
      prepTier: tier,
    });
    // QC hangs BELOW its step, in the same column.
    s.qc.forEach((q) => {
      const qk = keyFor(q);
      const qpos = place(q, col, pos.row + 1);
      nodes.push({
        nodeKey: qk, nodeType: q.type, nodeName: q.name,
        posX: qpos.x, posY: qpos.y,
        propsJson: JSON.stringify(q.props), prepTier: null,
      });
      edges.push({ fromNodeKey: key, toNodeKey: qk, portKey: 'out' });
    });
    return { key, row: pos.row };
  };

  /** Returns the key of the LAST node on this run, to chain the next one onto. */
  const run = (list: Step[], startCol: number, row: number): { last: string | null; col: number } => {
    let prev: string | null = null;
    let col = startCol;

    for (const s of list) {
      const { key, row: placedRow } = emit(s, col, row);
      if (prev) edges.push({ fromNodeKey: prev, toNodeKey: key, portKey: 'out' });

      if (s.branch?.length) {
        // Each branch runs on its own lane BELOW the parent — never above, which
        // is what produced off-canvas nodes. The first lane sits on the parent's
        // row so the main path reads straight through.
        let widest = col + 1;
        const ends: { port: string; last: string | null }[] = [];
        s.branch.forEach((b, i) => {
          const laneRow = placedRow + i;
          const r = run(b.steps, col + 1, laneRow);
          widest = Math.max(widest, r.col);
          ends.push({ port: b.port, last: r.last });
          // The branch's first node takes the port label.
          const first = b.steps[0];
          if (first) {
            const fk = first.key || '';
            const emitted = nodes.find(n => n.nodeName === first.name && n.nodeType === first.type);
            const target = fk || emitted?.nodeKey;
            if (target) {
              const existing = edges.find(e => e.fromNodeKey === key && e.toNodeKey === target);
              if (existing) existing.portKey = b.port;
              else edges.push({ fromNodeKey: key, toNodeKey: target, portKey: b.port });
            }
          }
        });
        col = widest;
        // Whatever comes next is fed by every branch end (the rejoin).
        prev = null;
        const nextIdx = list.indexOf(s) + 1;
        const next = list[nextIdx];
        if (next) {
          const nk = next.key || `n${seq + 1}`;
          for (const e of ends) if (e.last) edges.push({ fromNodeKey: e.last, toNodeKey: nk, portKey: 'out' });
        }
        continue;
      }

      prev = key;
      col += 1;
    }
    return { last: prev, col };
  };

  run(steps, 0, 0);
  return { nodes, edges };
}

/** Flatten a step tree for counting / validation. */
export function flatten(steps: Step[]): Step[] {
  const out: Step[] = [];
  const walk = (list: Step[]) => {
    for (const s of list) {
      out.push(s, ...s.qc);
      s.branch?.forEach(b => walk(b.steps));
    }
  };
  walk(steps);
  return out;
}
